export type UnitSystem = 'scientific' | 'imperial';

const METERS_PER_KILOMETER = 1000;
const METERS_PER_MILE = 1609.34;
const SECONDS_PER_MILE_FACTOR = METERS_PER_MILE / METERS_PER_KILOMETER;

const formatTimeFromSeconds = (seconds: number) => {
  const normalizedSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(normalizedSeconds / 60);
  const remainder = normalizedSeconds % 60;
  return `${minutes}:${remainder < 10 ? '0' : ''}${remainder}`;
};

export const convertDistance = (
  meters: number,
  unitSystem: UnitSystem
): number => {
  const value = Math.max(0, meters);
  if (unitSystem === 'scientific') {
    return value / METERS_PER_KILOMETER;
  }
  return value / METERS_PER_MILE;
};

export const convertPace = (
  secondsPerKilometer: number,
  unitSystem: UnitSystem
): number => {
  const value = Math.max(0, secondsPerKilometer);
  if (unitSystem === 'scientific') {
    return value;
  }
  return value * SECONDS_PER_MILE_FACTOR;
};

export const formatDistance = (
  meters: number | null | undefined,
  unitSystem: UnitSystem,
  precision: number = 1
): string => {
  if (meters === null || meters === undefined) {
    return '';
  }
  const result = convertDistance(meters, unitSystem);
  return `${result.toFixed(precision)} ${unitSystem === 'scientific' ? 'km' : 'mi'}`;
};

export const formatDistanceLabel = (
  meters: number | null | undefined,
  unitSystem: UnitSystem,
  precision?: number
): string => formatDistance(meters, unitSystem, precision);

export const formatPace = (
  secondsPerKilometer: number | null | undefined,
  unitSystem: UnitSystem
): string => {
  if (secondsPerKilometer === null || secondsPerKilometer === undefined) {
    return '';
  }
  const value = convertPace(secondsPerKilometer, unitSystem);
  return `${formatTimeFromSeconds(value)} /${unitSystem === 'scientific' ? 'km' : 'mi'}`;
};

export const formatPaceLabel = (
  secondsPerKilometer: number | null | undefined,
  unitSystem: UnitSystem
): string => formatPace(secondsPerKilometer, unitSystem);

export const metersToKilometers = (
  meters: number | null | undefined,
  precision: number = 2
): number | null => {
  if (meters === null || meters === undefined) {
    return null;
  }
  const factor = 10 ** precision;
  return Math.round((meters / METERS_PER_KILOMETER) * factor) / factor;
};
